// The working margin's state (docs/design/SIVUPALSTA.md "Shell"): which word is open, which line
// is active, whether quick settings are open. One React context for the whole shell, mirrored in
// the URL so Back closes it on the phone and the same link docks the PC margin:
//
//   ?sana=<form>            a High Valyrian word the person tapped (as written: "Valar")
//   ?sana=<form>|<lemmaId>  the same, with one reading of an ambiguous form chosen
//   ?rivi=<line id>         the active line (the one player plays it)
//   ?termi=<term id>        a grammar term the person tapped (grammar/terms: <Term>, <Define>)
//   history state { pika }  quick settings (no link of their own)
//
// Opening pushes one history entry; switching words while one is open replaces it, so a single
// Back always closes the margin. State that arrives from the URL alone (a reload, Back / Forward,
// an incoming link) has no tapped element: after render the anchor is looked up — the line's
// `[data-line="<id>"]`, or the first word link of that form in the page's main column — and given
// the same treatment as a tap (band, tether, docking height, focus return), and it is scrolled
// into the top 40 % of the screen. The anchor is kept per open part (`word:…`, `line:…`), so the
// band follows the part that is open, not whichever element was tapped last. Positions (aligned notes, the docked digest) stay in CSS; only the
// tether and the docking height are measured once when something opens (Spread.tsx).
//
// Two contexts: the actions are stable (every WordLink uses them without re-rendering when the
// margin changes); the state changes (the hosts, the player and active lines read it).
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useT } from '../i18n';
import { keepInTopBand } from '../components/Sheet';
import { stopPlayer } from './player';
import { NO_LINK_INSIDE, WORD_LINE, WORD_LINE_HINT_ID, WORD_LINK, wordsOfLine } from './WordLink';

/** A word the person pointed at: the form as written, and the reading they chose, if any. */
export interface WordRef {
  form: string;
  lemma?: string;
}

/** One source of a line's audio: a playable URL and who is speaking ("Peterson", "meidän ääntämys · Roosa"). */
export interface LineSource {
  url: string;
  label: string;
  /** A part of a sequence: its own line (the player bar shows it while it plays). */
  hv?: string;
}

/** A line that can be the active one: what the player plays and what the margin shows for it. */
export interface LineInfo {
  id: string;
  /** The line's High Valyrian text (the player's label). */
  hv: string;
  sources: readonly LineSource[];
  /** The active line's tool in the margin (PC) — optional; pages may show it inline on the phone. */
  tool?: ReactNode;
  /**
   * The sources are one recording in parts, played back to back (a scene in our voices, line by
   * line), not alternative takes of the same line. The player bar shows which part is playing.
   */
  sequence?: boolean;
  /** The pinned tool's title in the lane ("Daenerys · GoT 3×04"); default: the lane's name. */
  title?: string;
}

export const WORD_PARAM = 'sana';
export const LINE_PARAM = 'rivi';
/** A grammar term's explanation (Kielioppi terms, 2026-10-07): grammar/terms. */
export const TERM_PARAM = 'termi';
/** The margin's URL parameters: left out of scroll memory and of the tabs' remembered locations. */
export const MARGIN_PARAMS: readonly string[] = [WORD_PARAM, LINE_PARAM, TERM_PARAM];

export function parseWordRef(v: string | null): WordRef | null {
  if (!v) return null;
  const i = v.indexOf('|');
  const form = (i >= 0 ? v.slice(0, i) : v).trim();
  const lemma = i >= 0 ? v.slice(i + 1).trim() : '';
  if (!form) return null;
  return lemma ? { form, lemma } : { form };
}

export function wordRefParam(r: WordRef): string {
  return r.lemma ? `${r.form}|${r.lemma}` : r.form;
}

/** The location without the margin's parameters (scroll memory, the tabs' last locations). */
export function withoutMarginParams(search: string): string {
  if (!search || !MARGIN_PARAMS.some((p) => search.includes(`${p}=`))) return search;
  const sp = new URLSearchParams(search);
  for (const p of MARGIN_PARAMS) sp.delete(p);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export type MarginPart = 'word' | 'line' | 'quick' | 'term';

export interface MarginActions {
  /** Open a word: PC docks its digest in the margin level with `anchor`; phone opens the word sheet. */
  openWord(ref: WordRef, anchor?: HTMLElement | null): void;
  /** Make a line the active one (the player bar appears; its tool docks in the margin). */
  openLine(id: string, anchor?: HTMLElement | null): void;
  openQuick(anchor?: HTMLElement | null): void;
  /** Open a grammar term's explanation (PC: docked in the margin; phone: the term sheet). Replaces an open word. */
  openTerm(id: string, anchor?: HTMLElement | null): void;
  /** Close one part, several, or whatever is open: Back when the margin pushed the entry, else a replace. */
  close(part?: MarginPart | readonly MarginPart[]): void;
  /** Lines announce what they play (LinePlay / useLine); returns the unregister function. */
  registerLine(info: LineInfo): () => void;
  /** A Spread announces itself: the margin then docks in its lane B at ≥ 1180px. */
  registerSpread(): () => void;
}

export interface MarginState {
  word: WordRef | null;
  line: string | null;
  quick: boolean;
  /** The open grammar term's id (grammar/terms). */
  term: string | null;
  /** The element that opened the current part (tether, docking height, sheet scroll, focus return). */
  anchor: HTMLElement | null;
  /** The active line's registration (null when no mounted line has that id). */
  lineInfo: LineInfo | null;
  /** A Spread is mounted (its lane B hosts the margin on a wide screen). */
  spread: boolean;
  /** Something is pinned: quick settings, a word, a term, or the active line's tool. */
  pinned: boolean;
  /** The open part was opened from the keyboard (lane B then takes the focus: Spread.tsx). */
  keyboard: boolean;
}

const ActionsCtx = createContext<MarginActions | null>(null);
const StateCtx = createContext<MarginState | null>(null);

interface NavState {
  pika?: boolean;
  /** This entry was pushed by the margin: closing goes Back. */
  marginPush?: boolean;
  [k: string]: unknown;
}

const asState = (s: unknown): NavState => (s && typeof s === 'object' ? (s as NavState) : {});

const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'Tab']);

/**
 * A key in a line of word links (`line`; `word` is the focused word, or null on the line itself):
 * the word to focus, 'open-only' (Enter on a line of one word opens it), or null (not ours).
 * ← / → step between the words (no wrap), Home / End go to the ends, Enter on the line steps
 * into its first word.
 */
export function wordLineKey(key: string, line: Element, word: HTMLElement | null): HTMLElement | 'open-only' | null {
  const words = wordsOfLine(line);
  if (!words.length) return null;
  const first = words[0]!;
  const last = words[words.length - 1]!;
  if (!word) {
    if (key === 'Enter') return words.length === 1 ? 'open-only' : first;
    if (key === 'ArrowRight' || key === 'Home') return first;
    if (key === 'ArrowLeft' || key === 'End') return last;
    return null;
  }
  const at = words.indexOf(word);
  if (key === 'ArrowRight') return words[Math.min(words.length - 1, at + 1)] ?? null;
  if (key === 'ArrowLeft') return words[Math.max(0, at - 1)] ?? null;
  if (key === 'Home') return first;
  if (key === 'End') return last;
  return null;
}

/** Which open part an anchor belongs to: quick settings, then the word, the term, then the line (as pinned). */
export function anchorKey(word: WordRef | null, line: string | null, quick: boolean, term: string | null = null): string | null {
  if (quick) return 'quick';
  if (word) return `word:${word.form}`;
  if (term) return `term:${term}`;
  if (line) return `line:${line}`;
  return null;
}

/**
 * The element a part opened from the URL belongs to: the line's element, or the first link of the
 * word's form — in the page's main column first (not the margin's own digest), else anywhere in the
 * page. Null while it is not rendered yet.
 */
export function findAnchor(word: WordRef | null, line: string | null, root: ParentNode = document, term: string | null = null): HTMLElement | null {
  if (!word && term) {
    for (const scope of [...root.querySelectorAll('.spread-main'), root.querySelector('#main') ?? root])
      for (const el of scope.querySelectorAll<HTMLElement>('[data-term]')) if (el.dataset.term === term) return el;
    return null;
  }
  if (word) {
    const links = (scope: ParentNode) =>
      [...scope.querySelectorAll<HTMLElement>(WORD_LINK)].filter((el) => el.dataset.word === word.form && !el.parentElement?.closest(NO_LINK_INSIDE));
    const pick = (list: HTMLElement[]) => (word.lemma ? (list.find((el) => el.dataset.wordLemma === word.lemma) ?? list[0]) : list[0]);
    for (const scope of [...root.querySelectorAll('.spread-main'), root.querySelector('#main') ?? root]) {
      const hit = pick(links(scope));
      if (hit) return hit;
    }
    return null;
  }
  if (line) {
    for (const el of root.querySelectorAll<HTMLElement>('[data-line]')) if (el.dataset.line === line) return el;
  }
  return null;
}

interface Anchor {
  el: HTMLElement;
  /** anchorKey() of the part it was opened for. */
  key: string;
}

export function MarginProvider({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const nav = asState(loc.state);
  const sp = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const word = useMemo(() => parseWordRef(sp.get(WORD_PARAM)), [sp]);
  const line = sp.get(LINE_PARAM) || null;
  const term = sp.get(TERM_PARAM) || null;
  const quick = !!nav.pika;

  const t = useT();
  const [anchor, setAnchorState] = useState<Anchor | null>(null);
  // an open on its way: the anchor is set at once, the URL follows in a moment (no lookup meanwhile)
  const pendingKey = useRef<string | null>(null);
  const setAnchor = useCallback(
    (a: Anchor | null) => {
      pendingKey.current = a?.key ?? null;
      setAnchorState(a);
    },
    [setAnchorState],
  );
  const [keyboard, setKeyboard] = useState(false);
  // how the person last acted: a key (Enter on a word, a button pressed with the keyboard) or a pointer
  const viaKey = useRef(false);
  const [lines, setLines] = useState<ReadonlyMap<string, LineInfo>>(() => new Map());
  const [spreads, setSpreads] = useState(0);

  // the latest location, read by the stable actions (updated before any event can run)
  const live = useRef({ loc, nav });
  useLayoutEffect(() => {
    live.current = { loc, nav };
  });

  const go = useCallback(
    (mut: (p: URLSearchParams) => void, state: NavState, replace: boolean) => {
      const { loc: l } = live.current;
      const p = new URLSearchParams(l.search);
      mut(p);
      const s = p.toString();
      void navigate({ pathname: l.pathname, search: s ? `?${s}` : '', hash: l.hash }, { replace, state });
    },
    [navigate],
  );

  const open = useCallback(
    (mut: (p: URLSearchParams) => void, pika: boolean, el: HTMLElement | null | undefined, key: string) => {
      const { nav: n } = live.current;
      setAnchor(el ? { el, key } : null);
      setKeyboard(viaKey.current);
      // Already open (the margin pushed this entry): replace it, so one Back closes the margin.
      const replace = !!n.marginPush;
      go(mut, { ...n, pika, marginPush: true }, replace);
    },
    [go, setAnchor, setKeyboard],
  );

  const actions = useMemo<MarginActions>(
    () => ({
      openWord: (ref, el) =>
        open(
          (p) => {
            p.set(WORD_PARAM, wordRefParam(ref));
            p.delete(TERM_PARAM);
          },
          false,
          el,
          anchorKey(ref, null, false)!,
        ),
      openLine: (id, el) =>
        open(
          (p) => {
            p.set(LINE_PARAM, id);
          },
          false,
          el,
          anchorKey(null, id, false)!,
        ),
      openQuick: (el) => open(() => undefined, true, el, 'quick'),
      openTerm: (id, el) =>
        open(
          (p) => {
            p.set(TERM_PARAM, id);
            p.delete(WORD_PARAM);
          },
          false,
          el,
          anchorKey(null, null, false, id)!,
        ),
      close: (part) => {
        const { loc: l, nav: n } = live.current;
        const p = new URLSearchParams(l.search);
        const parts: readonly MarginPart[] = part === undefined ? ['quick', 'word', 'term', 'line'] : typeof part === 'string' ? [part] : part;
        const remaining = {
          word: p.has(WORD_PARAM) && !parts.includes('word'),
          line: p.has(LINE_PARAM) && !parts.includes('line'),
          term: p.has(TERM_PARAM) && !parts.includes('term'),
          quick: !!n.pika && !parts.includes('quick'),
        };
        setAnchor(null);
        // The margin pushed this entry and nothing else stays open: plain Back (the phone's own
        // gesture does the same).
        if (n.marginPush && !remaining.word && !remaining.line && !remaining.term && !remaining.quick) {
          void navigate(-1);
          return;
        }
        if (parts.includes('word')) p.delete(WORD_PARAM);
        if (parts.includes('line')) p.delete(LINE_PARAM);
        if (parts.includes('term')) p.delete(TERM_PARAM);
        const s = p.toString();
        void navigate({ pathname: l.pathname, search: s ? `?${s}` : '', hash: l.hash }, { replace: true, state: { ...n, pika: remaining.quick } });
      },
      registerLine: (info) => {
        setLines((m) => {
          const next = new Map(m);
          next.set(info.id, info);
          return next;
        });
        return () =>
          setLines((m) => {
            if (m.get(info.id) !== info) return m;
            const next = new Map(m);
            next.delete(info.id);
            return next;
          });
      },
      registerSpread: () => {
        setSpreads((n) => n + 1);
        return () => setSpreads((n) => n - 1);
      },
    }),
    [open, navigate, setAnchor],
  );

  // Every word link on every page opens through this one listener (margin/WordLink.tsx: the words
  // carry no handlers). Capture phase: a word inside a clickable card opens the word, not the card.
  useEffect(() => {
    const wordOf = (e: Event): HTMLElement | null => {
      const el = e.target instanceof Element ? e.target.closest<HTMLElement>(WORD_LINK) : null;
      return el && !el.parentElement?.closest(NO_LINK_INSIDE) ? el : null;
    };
    const open = (e: Event, el: HTMLElement) => {
      const form = el.dataset.word;
      if (!form) return;
      e.preventDefault();
      e.stopPropagation();
      const lemma = el.dataset.wordLemma;
      actions.openWord(lemma ? { form, lemma } : { form }, el);
    };
    const onClick = (e: MouseEvent) => {
      const el = wordOf(e);
      // a text selection is not a tap
      if (el && !window.getSelection()?.toString()) open(e, el);
    };
    const onKey = (e: KeyboardEvent) => {
      if (!e.repeat && !MODIFIER_KEYS.has(e.key)) viaKey.current = true;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const el = wordOf(e);
      if (el && e.key === 'Enter') {
        open(e, el);
        return;
      }
      // a line of words: one tab stop, the words in it by arrow keys (WordLink.tsx)
      const target = el ?? (e.target instanceof HTMLElement && e.target.matches(WORD_LINE) ? e.target : null);
      const line = el ? el.parentElement?.closest<HTMLElement>(WORD_LINE) : target;
      if (!target || !line) return;
      const to = wordLineKey(e.key, line, el);
      if (to === 'open-only') {
        open(e, wordsOfLine(line)[0]!);
        return;
      }
      if (!to) return;
      e.preventDefault();
      if (to !== el) to.focus();
    };
    const onPointer = () => {
      viaKey.current = false;
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [actions]);

  const lineInfo = line ? (lines.get(line) ?? null) : null;
  // The anchor counts only for the part it was opened for (closed from outside — Back, a link —
  // or another part showing now: it no longer does).
  const key = anchorKey(word, line, quick, term);
  const activeAnchor = key && anchor?.key === key && anchor.el.isConnected ? anchor.el : null;

  // Restored from the URL (reload, Back / Forward, an incoming link): no element was tapped. Find
  // it once it is rendered (a page's data may come later: the DOM is watched while the part is
  // open, which also finds it again when the page re-renders it away) and treat it as tapped; the
  // first time for this part, scroll it into the top 40 % of the screen.
  const scrolledFor = useRef<string | null>(null);
  const anchorNow = useRef(anchor);
  useLayoutEffect(() => {
    anchorNow.current = anchor;
  });
  useEffect(() => {
    if (!key) scrolledFor.current = null;
    if (!key || key === 'quick') return;
    // an open from a tap is on its way to the URL: its element is the anchor
    if (pendingKey.current && pendingKey.current !== key) return;
    pendingKey.current = null;
    const attempt = () => {
      const a = anchorNow.current;
      if (a?.key === key && a.el.isConnected) {
        scrolledFor.current = key;
        return;
      }
      const el = findAnchor(word, line, document, term);
      if (!el) return;
      anchorNow.current = { el, key };
      setAnchorState(anchorNow.current);
      setKeyboard(false);
      if (scrolledFor.current !== key) {
        scrolledFor.current = key;
        keepInTopBand(el);
      }
    };
    attempt();
    if (typeof MutationObserver === 'undefined') return;
    let raf = 0;
    const mo = new MutationObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        attempt();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-line', 'data-word', 'data-term'] });
    return () => {
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [key, anchor, word, line, term]);
  // The line was closed (Back, another page, the bar's ✕): the sound stops with it. Only a line that
  // was open and is no longer — a tap starts the sound first and ?rivi= follows a moment later (the
  // router's navigation may render after the player's own state), which is not a close.
  const lineWas = useRef(line);
  useEffect(() => {
    const before = lineWas.current;
    lineWas.current = line;
    if (before && !line) stopPlayer();
  }, [line]);

  // The active anchor carries a mark (its ending takes the accent underline; DESIGN's letter rule).
  useEffect(() => {
    if (!activeAnchor) return;
    activeAnchor.setAttribute('data-margin-active', '');
    return () => activeAnchor.removeAttribute('data-margin-active');
  }, [activeAnchor]);
  const state = useMemo<MarginState>(
    () => ({
      word,
      line,
      quick,
      term,
      anchor: activeAnchor,
      lineInfo,
      spread: spreads > 0,
      pinned: quick || !!word || !!term || !!lineInfo?.tool,
      keyboard: keyboard && !!activeAnchor,
    }),
    [word, line, quick, term, activeAnchor, lineInfo, spreads, keyboard],
  );

  return (
    <ActionsCtx.Provider value={actions}>
      <StateCtx.Provider value={state}>
        {children}
        {/* what a line of word links does with the keyboard (each line points here: aria-describedby) */}
        <span id={WORD_LINE_HINT_ID} hidden>
          {t.margin.wordLineHint}
        </span>
      </StateCtx.Provider>
    </ActionsCtx.Provider>
  );
}

/** The margin's actions (stable; null outside the shell, e.g. in a unit test without a provider). */
export function useMarginActions(): MarginActions | null {
  return useContext(ActionsCtx);
}

const CLOSED: MarginState = { word: null, line: null, quick: false, term: null, anchor: null, lineInfo: null, spread: false, pinned: false, keyboard: false };

/** The margin's state and actions together (pages, hosts, the player). */
export function useMargin(): MarginState & { actions: MarginActions | null } {
  const s = useContext(StateCtx) ?? CLOSED;
  return { ...s, actions: useContext(ActionsCtx) };
}
